import { MonitoringPromptRunUnitType } from '@edgebox/sync-core-data-definitions';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import type { AnswerPassageIndex } from './answerPassages.utils';

/**
 * Which elements of the rendered answer are passages, and which lines each one answers for.
 *
 * `HighlightedAnswer` draws the answer through react-markdown and decides, element by element, which
 * ones are passages a reader can point at, light and pin. The analysed answer is split into units by
 * another parser, whose cuts do not always fall where the rendered elements do — consecutive quoted
 * lines are one quote on the page but a unit each in the analysis — so what is drawn is read from the
 * rendered elements themselves. The rules live here once: the renderer applies them while drawing,
 * and the same rules, run over the same parse, tell the page which passages are drawn without
 * waiting for the drawing.
 */

/** An element as react-markdown hands it to a component: its tag, its place in the markdown, its children. */
export interface PositionedNode {
  type?: string;
  tagName?: string;
  position?: { start?: { line?: number }; end?: { line?: number } };
  children?: readonly PositionedNode[];
}

/** The plugins the answer's markdown is parsed with, by the renderer and by the reading below alike. */
export const ANSWER_REMARK_PLUGINS = [remarkGfm];

/** Zero-based source line range of an element, or undefined when the node has no position. */
export function lineRangeOf(node: PositionedNode | undefined): { start: number; end: number } | undefined {
  const start = node?.position?.start?.line;
  const end = node?.position?.end?.line;
  if (typeof start !== 'number') {
    return undefined;
  }
  return { start: start - 1, end: (typeof end === 'number' ? end : start) - 1 };
}

/**
 * The markdown elements the answer draws as passages, and the tag each is drawn as: a top-level
 * heading is drawn as a second-level one. Headings below `###` and code blocks are drawn as plain
 * text, with no card and no highlight of their own.
 */
export const PASSAGE_RENDER_TAGS: Readonly<Record<string, keyof JSX.IntrinsicElements>> = {
  p: 'p',
  li: 'li',
  h1: 'h2',
  h2: 'h2',
  h3: 'h3',
  blockquote: 'blockquote',
  tr: 'tr',
};

/**
 * Which drawn tag renders a passage of which kind. A paragraph inside a list item or a quote is part
 * of that item's passage, so only the item answers for it — never both, and never the inner one.
 */
const PASSAGE_ELEMENT_TYPES: Readonly<Record<string, readonly MonitoringPromptRunUnitType[]>> = {
  p: [MonitoringPromptRunUnitType.Paragraph],
  li: [MonitoringPromptRunUnitType.Bullet],
  blockquote: [MonitoringPromptRunUnitType.Quote],
  h2: [MonitoringPromptRunUnitType.Heading],
  h3: [MonitoringPromptRunUnitType.Heading],
  tr: [MonitoringPromptRunUnitType.TableRow],
};

/** The passage an element drawn as `tag` stands for, when it stands for one. */
export function passageIdOf(passages: AnswerPassageIndex | undefined, tag: string, node: PositionedNode | undefined): string | undefined {
  const range = lineRangeOf(node);
  const unitId = range ? passages?.idAtLine.get(range.start) : undefined;
  const passage = unitId ? passages?.byId.get(unitId) : undefined;
  if (!passage) {
    return undefined;
  }
  return !passage.type || (PASSAGE_ELEMENT_TYPES[tag] ?? []).includes(passage.type) ? passage.unitId : undefined;
}

/** The passage an element of the parsed answer is drawn as, when the answer draws it as one. */
export function drawnPassageIdOf(passages: AnswerPassageIndex | undefined, node: PositionedNode): string | undefined {
  const tag = node.type === 'element' && node.tagName ? PASSAGE_RENDER_TAGS[node.tagName] : undefined;
  return tag ? passageIdOf(passages, tag, node) : undefined;
}

/** Whether an element holds a passage drawn inside it, at any depth. */
export function holdsDrawnPassage(passages: AnswerPassageIndex | undefined, node: PositionedNode): boolean {
  return (node.children ?? []).some(
    (child) => child.type === 'element' && (!!drawnPassageIdOf(passages, child) || holdsDrawnPassage(passages, child))
  );
}

/**
 * The lines an element holds itself: its range, less the ranges of the passages drawn inside it,
 * which light on their own. Everything else inside it — a paragraph of a list item, a quoted line
 * drawn within a longer quote, an item with no card of its own — is part of it.
 */
export function passageLinesOf(passages: AnswerPassageIndex | undefined, node: PositionedNode): Set<number> {
  const lines = new Set<number>();
  const range = lineRangeOf(node);
  if (!range) {
    return lines;
  }
  for (let line = range.start; line <= range.end; line += 1) {
    lines.add(line);
  }
  const release = (parent: PositionedNode) => {
    for (const child of parent.children ?? []) {
      if (child.type !== 'element') {
        continue;
      }
      if (!drawnPassageIdOf(passages, child)) {
        release(child);
        continue;
      }
      const childRange = lineRangeOf(child);
      if (childRange) {
        for (let line = childRange.start; line <= childRange.end; line += 1) {
          lines.delete(line);
        }
      }
    }
  };
  release(node);
  return lines;
}

/** One passage as the answer draws it: the passage, and the lines its element holds itself. */
export interface AnswerPassageElement {
  passageId: string;
  /** The element's lines, less those of the passages drawn inside it, which light on their own. */
  lines: ReadonlySet<number>;
}

/**
 * Every passage the answer draws with a card, in reading order: the elements `HighlightedAnswer`
 * makes passages of, read from the same parse of the markdown and by the same rules.
 */
export function answerPassageElements(markdown: string, passages: AnswerPassageIndex): AnswerPassageElement[] {
  if (!markdown || !passages.order.length) {
    return [];
  }
  const processor = unified().use(remarkParse).use(ANSWER_REMARK_PLUGINS).use(remarkRehype, { allowDangerousHtml: true });
  const tree: PositionedNode = processor.runSync(processor.parse(markdown));

  const elements: AnswerPassageElement[] = [];
  const visit = (node: PositionedNode) => {
    const passageId = drawnPassageIdOf(passages, node);
    if (passageId) {
      elements.push({ passageId, lines: passageLinesOf(passages, node) });
    }
    for (const child of node.children ?? []) {
      visit(child);
    }
  };
  visit(tree);
  return elements;
}
